import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { useBrandingStore } from '../store/branding'

interface BrandingProfile {
  id: string
  name: string
  config: string
}

interface BrandingConfig {
  companyName: string
  logoR2Key?: string
  accentColor?: string
  address?: string
  cityStateZip?: string
  email?: string
  phone?: string
  footerText?: string
}

function parseConfig(raw: string): BrandingConfig {
  try { return JSON.parse(raw) } catch { return { companyName: '' } }
}

interface Props {
  currentOverride: string | null
  onApply: (brandingJson: string | null) => void
  onClose: () => void
}

export default function BrandingOverrideModal({ currentOverride, onApply, onClose }: Props) {
  const defaultBranding = useBrandingStore()
  const [profiles, setProfiles] = useState<BrandingProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [mode, setMode] = useState<'default' | 'custom'>(currentOverride ? 'custom' : 'default')
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null)
  const [selectedConfig, setSelectedConfig] = useState<BrandingConfig | null>(
    currentOverride ? (() => { try { return JSON.parse(currentOverride) } catch { return null } })() : null
  )

  useEffect(() => {
    api.get<BrandingProfile[]>('/branding-profiles')
      .then(data => {
        setProfiles(data)
        // If there's a current override, try to find matching profile
        if (currentOverride && data.length > 0) {
          try {
            const current = JSON.parse(currentOverride) as BrandingConfig
            const match = data.find(p => {
              const cfg = parseConfig(p.config)
              return cfg.companyName === current.companyName
            })
            if (match) setSelectedProfileId(match.id)
          } catch { /* ignore */ }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [currentOverride])

  function handleProfileSelect(profileId: string) {
    setSelectedProfileId(profileId)
    const profile = profiles.find(p => p.id === profileId)
    if (profile) setSelectedConfig(parseConfig(profile.config))
  }

  function handleApply() {
    if (mode === 'default') {
      onApply(null)
    } else if (selectedConfig) {
      onApply(JSON.stringify(selectedConfig))
    }
  }

  const preview = mode === 'custom' ? selectedConfig : null

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'var(--bg-overlay, rgba(0,0,0,0.5))',
        zIndex: 1000, backdropFilter: 'blur(2px)',
      }} />
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: 'var(--bg-surface)', borderRadius: '20px 20px 0 0', zIndex: 1001,
        padding: '16px 20px', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
        maxHeight: '85vh', overflowY: 'auto',
        boxShadow: 'var(--shadow-lg)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--border-default)', margin: '0 auto 16px' }} />
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
          Client-Facing Branding
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
          Choose which branding the client sees on this proposal.
        </p>

        {/* Radio: Use default */}
        <label style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
          borderRadius: 10, cursor: 'pointer', marginBottom: 6,
          border: mode === 'default' ? '2px solid var(--accent)' : '2px solid var(--border-default)',
          background: mode === 'default' ? 'var(--accent-m)' : 'var(--bg-surface)',
        }}>
          <input
            type="radio" name="branding-mode" checked={mode === 'default'}
            onChange={() => { setMode('default'); setSelectedConfig(null) }}
            style={{ accentColor: 'var(--accent)' }}
          />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
              Use default ({defaultBranding.companyName})
            </div>
          </div>
        </label>

        {/* Radio: Use custom */}
        <label style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
          borderRadius: 10, cursor: 'pointer', marginBottom: 16,
          border: mode === 'custom' ? '2px solid var(--accent)' : '2px solid var(--border-default)',
          background: mode === 'custom' ? 'var(--accent-m)' : 'var(--bg-surface)',
        }}>
          <input
            type="radio" name="branding-mode" checked={mode === 'custom'}
            onChange={() => setMode('custom')}
            style={{ accentColor: 'var(--accent)' }}
          />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Use custom branding</div>
          </div>
        </label>

        {/* Profile selector */}
        {mode === 'custom' && (
          <>
            {loading ? (
              <p style={{ fontSize: 13, color: 'var(--text-tertiary)', padding: '10px 0' }}>Loading profiles...</p>
            ) : profiles.length === 0 ? (
              <div style={{ padding: '16px', border: '1px solid var(--border-default)', borderRadius: 10, textAlign: 'center' }}>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>No branding profiles yet.</p>
                <a
                  href="/settings/branding-profiles"
                  style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}
                >
                  Create New Profile
                </a>
              </div>
            ) : (
              <>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>
                  Select profile:
                </label>
                <select
                  value={selectedProfileId || ''}
                  onChange={e => handleProfileSelect(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 8,
                    border: '1px solid var(--border-default)', fontSize: 14, color: 'var(--text-primary)',
                    background: 'var(--bg-surface)', marginBottom: 16,
                  }}
                >
                  <option value="" disabled>Choose a profile...</option>
                  {profiles.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </>
            )}

            {/* Preview */}
            {preview && (
              <div style={{
                padding: 14, border: '1px solid var(--border-default)', borderRadius: 10,
                background: 'var(--bg-base)', marginBottom: 16,
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 10 }}>Preview:</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  {preview.logoR2Key ? (
                    <img
                      src={`/api/files/${preview.logoR2Key}`}
                      alt=""
                      style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 6 }}
                    />
                  ) : preview.accentColor ? (
                    <div style={{
                      width: 32, height: 32, borderRadius: 6, background: preview.accentColor,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 12, fontWeight: 700,
                    }}>
                      {(preview.companyName || '?')[0]}
                    </div>
                  ) : null}
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{preview.companyName}</div>
                    {preview.cityStateZip && (
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{preview.cityStateZip}</div>
                    )}
                  </div>
                </div>
                {preview.accentColor && (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    Accent color: <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: preview.accentColor }} /> {preview.accentColor}
                  </div>
                )}
                {preview.footerText && (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    Footer: &ldquo;{preview.footerText}&rdquo;
                  </div>
                )}
              </div>
            )}

            {profiles.length > 0 && (
              <a
                href="/settings/branding-profiles"
                style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600, textDecoration: 'none', display: 'block', marginBottom: 16 }}
              >
                Or Create New Profile
              </a>
            )}
          </>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '14px 0', borderRadius: 12, border: '1px solid var(--border-default)',
              background: 'var(--bg-surface)', color: 'var(--text-secondary)', fontSize: 15, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={mode === 'custom' && !selectedConfig}
            style={{
              flex: 1, padding: '14px 0', borderRadius: 12, border: 'none',
              background: mode === 'custom' && !selectedConfig
                ? 'var(--border-default)' : 'linear-gradient(135deg, var(--accent), var(--accent-h))',
              color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit',
              opacity: mode === 'custom' && !selectedConfig ? 0.5 : 1,
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </>
  )
}
